import { type FC } from 'react';
import { useSelector } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import classnames from 'classnames';

import dibsCss from 'dibs-css';

// components
import { PdpRespSidebarBlurbShipping } from './PdpRespSidebarBlurbShipping.tsx';
import { PdpRespSidebarBlurbReturnPolicy } from './PdpRespSidebarBlurbReturnPolicy.tsx';
import { useRenderBlurbs } from './hooks/useRenderBlurbs.ts';
import { type RelayVariables, type Store } from '../../global/relayVariableTypes.ts';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb.tsx';

import PdpSharedSidebarBuyerIncentives from '../shared/PdpSharedSidebarBuyerIncentives/PdpSharedSidebarBuyerIncentives.tsx';

import { type PdpRespSidebarBlurbs_item$data } from './__generated__/PdpRespSidebarBlurbs_item.graphql.ts';
import { type PdpRespSidebarBlurbs_user$data } from './__generated__/PdpRespSidebarBlurbs_user.graphql.ts';
import { type PdpRespSidebarBlurbs_displayInfo$data } from './__generated__/PdpRespSidebarBlurbs_displayInfo.graphql.ts';
import { type PdpRespSidebarBlurbs_sku$data } from './__generated__/PdpRespSidebarBlurbs_sku.graphql.ts';

// styles
import styles from './PdpRespSidebarBlurbs.module.css';

type Props = {
    item: PdpRespSidebarBlurbs_item$data;
    user: PdpRespSidebarBlurbs_user$data | null | undefined;
    displayInfo: PdpRespSidebarBlurbs_displayInfo$data | null | undefined;
    sku: PdpRespSidebarBlurbs_sku$data | null | undefined;
    currency?: string;
    itemQuantity?: number;
};

const PdpRespSidebarBlurbsComponent: FC<Props> = ({
    currency = 'USD',
    item,
    user,
    displayInfo = null,
    sku,
    itemQuantity,
}) => {
    const renderBlurbs = useRenderBlurbs({ displayInfo });
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );
    if (!renderBlurbs) {
        return null;
    }

    return (
        <div className={classnames(styles.container, dibsCss.mbSmaller)}>
            <PdpRespSidebarBlurbShipping
                currency={currency}
                item={item}
                user={user}
                renderTooltip
                sku={relayVariables.isMultiSku ? sku : null}
                isClient={relayVariables.afterDisplay}
                itemQuantity={itemQuantity}
                shortLinkCta
            />

            <div>
                <PdpSharedBlurb
                    zeroPadding
                    IconComponent={null}
                    iconClass={classnames(dibsCss.mrXsmall, styles.icon)}
                >
                    <PdpSharedSidebarBuyerIncentives />
                </PdpSharedBlurb>
            </div>

            <div className={classnames(dibsCss.hidden, styles.returnPolicyContainer)}>
                <PdpRespSidebarBlurbReturnPolicy item={item} />
            </div>
        </div>
    );
};

export const PdpRespSidebarBlurbs = createFragmentContainer(PdpRespSidebarBlurbsComponent, {
    item: graphql`
        fragment PdpRespSidebarBlurbs_item on Item {
            ...PdpRespSidebarBlurbShipping_item
            ...PdpRespSidebarBlurbReturnPolicy_item
            ...useShowReturns_item
        }
    `,
    user: graphql`
        fragment PdpRespSidebarBlurbs_user on User {
            ...PdpRespSidebarBlurbShipping_user
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarBlurbs_displayInfo on ItemDisplayInfo {
            ...useRenderBlurbs_displayInfo
        }
    `,
    sku: graphql`
        fragment PdpRespSidebarBlurbs_sku on ItemSkuType {
            ... on ItemSkuType @include(if: $isMultiSku) {
                ...PdpRespSidebarBlurbShipping_sku
            }
        }
    `,
});
